/**
 * LoginScreen — the sign-in gate that did not previously exist.
 *
 * The app used to boot straight into the authenticated shell, so this is the
 * first screen an unauthenticated visitor now sees.
 *
 * Deliberately says nothing about which accounts exist: no seeded credentials,
 * no "try admin@…" hint, no distinction between "unknown email" and "wrong
 * password" beyond whatever the server chooses to return. The 429 lockout
 * message is surfaced verbatim because it is the one case where the server's
 * wording (how long, why) is genuinely useful to the person locked out.
 */

import { useState } from 'react'
import { LogIn, Loader2, AlertTriangle } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import YetiMascot from './YetiMascot'

export default function LoginScreen() {
  const { signIn, error: sessionError } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)

  // A rate-limit response gets its own treatment: it is not a credential
  // mistake, and the server explains the lockout window.
  const isLockout = formError?.status === 429
  const message = formError?.message || (formError ? 'Sign-in failed.' : sessionError)

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submitting) return // guards double submit via Enter + click

    setFormError(null)

    if (!email.trim() || !password) {
      setFormError({ message: 'Enter your email address and password.' })
      return
    }

    setSubmitting(true)
    try {
      await signIn(email.trim(), password)
      // On success this component unmounts; no state update needed.
    } catch (err) {
      setFormError({ message: err?.message || 'Sign-in failed.', status: err?.status })
      setPassword('')
      setSubmitting(false)
    }
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        {/* Interactive Pure SVG Yeti Mascot */}
        <YetiMascot
          isPasswordFocused={isPasswordFocused}
          emailLength={email.length}
          isError={Boolean(formError || sessionError)}
        />

        <div className="login-brand">
          <img src="/logo.png" alt="" className="login-logo" />
          <div>
            <p className="login-wordmark">CloudIQ Studio</p>
            <p className="login-tagline">executive reports at digital speed</p>
          </div>
        </div>

        <div className="login-body">
          <h1 className="login-heading">Sign in</h1>
          <p className="login-sub">Use your CloudIQ Studio account to continue.</p>

          {message && (
            <div
              role="alert"
              className={`login-alert ${isLockout ? 'login-alert-lockout' : ''}`}
            >
              <AlertTriangle size={15} aria-hidden="true" />
              <span>{message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="login-email">Email address</label>
              <input
                id="login-email"
                name="email"
                type="email"
                className="form-control"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                // First field on a dedicated sign-in page: focusing it is the
                // expected behaviour rather than a focus hijack.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                required
                disabled={submitting}
                placeholder="you@company.com"
                spellCheck="false"
              />
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                name="password"
                type="password"
                className="form-control"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onFocus={() => setIsPasswordFocused(true)}
                onBlur={() => setIsPasswordFocused(false)}
                autoComplete="current-password"
                required
                disabled={submitting}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary login-submit"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="spin" aria-hidden="true" />
                  Signing in…
                </>
              ) : (
                <>
                  <LogIn size={16} aria-hidden="true" />
                  Sign in
                </>
              )}
            </button>
          </form>
        </div>

        <p className="login-footnote">
          Sessions are scoped to this browser tab and expire after a period of inactivity.
        </p>
      </div>
    </div>
  )
}
